import { GroupingDirection, SortingDirection } from 'api/clients/FilesViewsClient'
import { Property } from 'api/clients/PropertyClient'
import cx from 'classnames'
import { useEffect, useState } from 'react'
import { useFinderContext } from 'screens/Finder/FinderContext'
import { useHandbookReadOnlyUser } from 'hooks/useHandbookReadOnlyUser'

import { Button } from 'components/Button'
import ExistingPropertyActionItem from 'common/PropertyActions/ExistingPropertyActionItem'
import { DropdownTrigger } from 'components/DropdownTrigger/DropdownTrigger'
import { useProperties } from 'hooks/queries/companies/properties/useProperties'
import { ReactComponent as TrashIcon } from 'assets/icons/trash16.svg'
import { BackButton } from '../common/BackButton/BackButton'
import { DirectionPicker } from '../common/DirectionPicker/DirectionPicker'
import { GroupPropertyFinder } from '../common/PropertyFinder/GroupPropertyFinder'

import { IconButton } from 'components/IconButton/IconButton'
import SharedStyles from '../ViewControls.module.scss'
import Styles from './Group.module.scss'
import PropertyPickerStyles from 'screens/Finder/components/ViewPreferences/components/ViewModifierControls/common/PropertyPicker/PropertyPicker.module.scss'

const GroupSortDirectionMapping = {
  [GroupingDirection.Ascending]: SortingDirection.Ascending,
  [GroupingDirection.Descending]: SortingDirection.Descending,
}

const SortGroupDirectionMapping = {
  [SortingDirection.Ascending]: GroupingDirection.Ascending,
  [SortingDirection.Descending]: GroupingDirection.Descending,
}

export const Group = ({ onBackButtonPress }: { onBackButtonPress?: () => void }) => {
  const { filesView, updateSchema } = useFinderContext()
  const { isReadOnlyUser } = useHandbookReadOnlyUser()
  const mappedDirection =
    filesView?.schema.grouping_direction && GroupSortDirectionMapping[filesView?.schema.grouping_direction]

  const [selectedProperty, setSelectedProperty] = useState<Property | undefined>(filesView?.schema.grouping_property)
  const [selectedDirection, setSelectedDirection] = useState<SortingDirection | undefined>(mappedDirection)
  useProperties(undefined, { enabled: !selectedProperty }, { pushToStore: true })

  useEffect(() => {
    setSelectedProperty(filesView?.schema.grouping_property)
    setSelectedDirection(mappedDirection)
  }, [filesView?.schema.grouping_property, mappedDirection])

  const onPropertySelected = (property: Property) => {
    setSelectedProperty(property)
    updateSchema({ ...filesView?.schema, grouping_property_id: property.id })
  }

  const onDirectionSelected = (direction: SortingDirection) => {
    setSelectedDirection(direction)
    updateSchema({ ...filesView?.schema, grouping_direction: SortGroupDirectionMapping[direction] })
  }

  const removeGrouping = async () => {
    setSelectedProperty(undefined)
    updateSchema({ ...filesView?.schema, grouping_property_id: null, grouping_direction: undefined })
  }

  return (
    <div className={cx(SharedStyles.control, Styles.groupControl)} data-testid="view-modifier-group">
      <div className={cx(SharedStyles.title, { [SharedStyles.noBackButton]: !onBackButtonPress })}>
        {!!onBackButtonPress && <BackButton onPress={onBackButtonPress} />} Group
      </div>
      <div className={SharedStyles.content}>
        {!selectedProperty ? (
          <GroupPropertyFinder onPropertySelected={onPropertySelected} />
        ) : (
          <div className={Styles.grouping}>
            <DropdownTrigger placement="bottom-start" popoverClassName={PropertyPickerStyles.popover} noPressed>
              <Button variant="secondaryGrey" size="lg" withChevron="down" className={PropertyPickerStyles.trigger}>
                <ExistingPropertyActionItem
                  property={selectedProperty}
                  className={PropertyPickerStyles.selectedProperty}
                />
              </Button>
              {(close) => (
                <GroupPropertyFinder
                  onPropertySelected={(property: Property) => {
                    onPropertySelected(property)
                    close()
                  }}
                  selectedProperty={selectedProperty}
                  className={PropertyPickerStyles.picker}
                />
              )}
            </DropdownTrigger>
            <DirectionPicker
              property={selectedProperty}
              selectedDirection={selectedDirection}
              onDirectionSelected={onDirectionSelected}
            />
            <IconButton
              className={Styles.removeGrouping}
              icon={TrashIcon}
              onPress={removeGrouping}
              data-testid="remove-grouping-btn"
              isDisabled={isReadOnlyUser}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default Group
